import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOperator } from '@/lib/auth';

/**
 * Admin Sequences — operator surface for the multi-step GHL replacements
 * (Lead Drip + Nurture + any future enrollment-style workflow).
 *
 * Three things you can do here:
 *   1. See each sequence's full step calendar (which template fires when).
 *   2. Toggle the GHL binding on/off (without this, the sequence never fires).
 *   3. See who's currently enrolled and cancel an in-flight enrollment.
 *
 * What this is NOT:
 *   - The single-shot tag-add bindings (request_coi, send_welcome_letter,
 *     etc.) live in the bindings table too but are simple enough that they
 *     don't need a per-step calendar view. Edit them via SQL today.
 *   - Editing email body / subject — that's `/admin/messages`.
 */

interface Binding {
  id: string;
  location_id: string;
  event_type: string;
  event_match: { tag?: string };
  target_rpc: string;
  workflow_name: string;
  debounce_minutes: number;
  enabled: boolean;
  notes: string | null;
}

interface ActiveEnrollment {
  tenant_id: string;
  tenant_name: string | null;
  tenant_email: string;
  location_id: string;
  enrolled_at: string;
  next_step_name: string;
  next_step_subject: string;
  next_fires_at: string;
  remaining_count: number;
  cancelled_count: number;
  sent_count: number;
}

interface SequenceTemplate {
  workflow_name: string;
  subject: string;
}

// Static description of each sequence — keeps the calendar view a known shape.
// If we ever generalize this (read sequences from a `sequence_steps` table),
// these constants come from the DB instead.
const SEQUENCES = [
  {
    id: 'lead_drip',
    title: 'Lead Drip',
    binding_workflow_name: 'lead_drip_enrolled',
    workflow_prefix: 'lead_drip_',
    trigger_tag: 'email-lead',
    cadence_label: '6 emails over 23 days',
    goal_tags: ['tour-booked', 'enrolled', 'do-not-contact', 'lost-lead'],
    steps: [
      { day: 0,  workflow_name: 'lead_drip_day0_welcome' },
      { day: 2,  workflow_name: 'lead_drip_day2_virtual_tour' },
      { day: 5,  workflow_name: 'lead_drip_day5_revenue' },
      { day: 9,  workflow_name: 'lead_drip_day9_experience' },
      { day: 16, workflow_name: 'lead_drip_day16_all_inclusive' },
      { day: 23, workflow_name: 'lead_drip_day23_referral' },
    ],
  },
  {
    id: 'nurture',
    title: 'Nurture',
    binding_workflow_name: 'nurture_enrolled',
    workflow_prefix: 'nurture_',
    trigger_tag: 'nurture',
    cadence_label: '6 emails monthly over 6 months',
    goal_tags: ['engaged', 'tour-booked', 'enrolled', 'do-not-contact'],
    steps: [
      { day: 30,  workflow_name: 'nurture_month1_client_list',  label: 'Month 1' },
      { day: 60,  workflow_name: 'nurture_month2_community',    label: 'Month 2' },
      { day: 90,  workflow_name: 'nurture_month3_math',         label: 'Month 3' },
      { day: 120, workflow_name: 'nurture_month4_availability', label: 'Month 4' },
      { day: 150, workflow_name: 'nurture_month5_trends',       label: 'Month 5' },
      { day: 180, workflow_name: 'nurture_month6_final',        label: 'Month 6' },
    ],
  },
] as const;

export default function Sequences() {
  const navigate = useNavigate();
  const { operator, loading: opLoading } = useOperator();
  const qc = useQueryClient();

  useEffect(() => {
    if (!opLoading && !operator) navigate('/', { replace: true });
  }, [opLoading, operator, navigate]);

  // All bindings whose workflow_name matches one of our sequence enrollments.
  const { data: bindings } = useQuery<Binding[]>({
    queryKey: ['ghl-event-bindings', 'sequences'],
    enabled: !!operator,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ghl_event_bindings')
        .select('id,location_id,event_type,event_match,target_rpc,workflow_name,debounce_minutes,enabled,notes')
        .in('workflow_name', SEQUENCES.map((s) => s.binding_workflow_name));
      if (error) throw error;
      return (data ?? []) as Binding[];
    },
  });

  // Subjects for each step, so the calendar shows what each email actually says.
  const { data: stepTemplates } = useQuery<SequenceTemplate[]>({
    queryKey: ['message-templates', 'sequence-steps'],
    enabled: !!operator,
    queryFn: async () => {
      const allWorkflows = SEQUENCES.flatMap((s) => s.steps.map((st) => st.workflow_name));
      const { data, error } = await supabase
        .from('message_templates')
        .select('workflow_name,subject')
        .in('workflow_name', allWorkflows);
      if (error) throw error;
      return (data ?? []) as SequenceTemplate[];
    },
  });

  // Active enrollments per sequence — calls the list_active_enrollments RPC.
  const { data: dripActive } = useQuery<ActiveEnrollment[]>({
    queryKey: ['active-enrollments', 'lead_drip_'],
    enabled: !!operator,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_active_enrollments', {
        p_workflow_prefix: 'lead_drip_',
      });
      if (error) throw error;
      return (data ?? []) as ActiveEnrollment[];
    },
  });

  const { data: nurtureActive } = useQuery<ActiveEnrollment[]>({
    queryKey: ['active-enrollments', 'nurture_'],
    enabled: !!operator,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_active_enrollments', {
        p_workflow_prefix: 'nurture_',
      });
      if (error) throw error;
      return (data ?? []) as ActiveEnrollment[];
    },
  });

  if (opLoading) return <div className="text-image-charcoal text-sm">Checking access…</div>;
  if (!operator) return null;

  const subjectFor = (workflowName: string) =>
    stepTemplates?.find((t) => t.workflow_name === workflowName)?.subject ?? '(template missing)';
  const bindingFor = (workflowName: string) =>
    bindings?.find((b) => b.workflow_name === workflowName);
  const activeFor = (sequenceId: string) =>
    sequenceId === 'lead_drip' ? dripActive : nurtureActive;

  async function toggleBinding(binding: Binding) {
    const { error } = await supabase
      .from('ghl_event_bindings')
      .update({ enabled: !binding.enabled })
      .eq('id', binding.id);
    if (error) {
      alert(`Failed to toggle: ${error.message}`);
      return;
    }
    qc.invalidateQueries({ queryKey: ['ghl-event-bindings'] });
  }

  async function cancelEnrollment(tenantId: string, prefix: string, tenantName: string | null) {
    const label = tenantName || tenantId.slice(0, 8);
    if (!confirm(`Cancel all queued ${prefix} messages for ${label}? This stops the sequence immediately.`)) return;
    const { data, error } = await supabase.rpc('cancel_drip_for_tenant', {
      p_tenant_id: tenantId,
      p_workflow_prefix: prefix,
    });
    if (error) {
      alert(`Cancel failed: ${error.message}`);
      return;
    }
    qc.invalidateQueries({ queryKey: ['active-enrollments'] });
    alert(`Cancelled ${data ?? 0} queued message${data === 1 ? '' : 's'}.`);
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-image-charcoal font-semibold">Admin</div>
            <h1 className="font-display text-2xl font-bold text-image-black">
              Multi-step <span className="text-image-pink">Sequences</span>
            </h1>
          </div>
          <Link to="/admin/messages" className="text-xs text-image-pink hover:text-image-plum font-semibold uppercase tracking-wider">
            ← Templates
          </Link>
        </div>
        <p className="text-sm text-image-charcoal mt-1">
          Drip + nurture enrollments. Each sequence drops multiple emails into the queue
          and self-cancels if the lead converts (goal tags). Toggle a binding off to
          temporarily pause new enrollments.
        </p>
      </div>

      {SEQUENCES.map((seq) => {
        const binding = bindingFor(seq.binding_workflow_name);
        const active = activeFor(seq.id) ?? [];
        return (
          <div key={seq.id} className="card space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-xl font-bold text-image-black">{seq.title}</h2>
                  {binding && (
                    <button
                      onClick={() => toggleBinding(binding)}
                      className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold transition-colors ${
                        binding.enabled
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                          : 'bg-image-light-gray text-image-charcoal hover:bg-image-fog'
                      }`}
                    >
                      {binding.enabled ? '● Enabled' : '○ Disabled'} · click to toggle
                    </button>
                  )}
                </div>
                <div className="text-sm text-image-charcoal mt-1">{seq.cadence_label}</div>
              </div>
            </div>

            <div className="bg-image-fog rounded-lg p-3 text-xs space-y-1.5">
              <div>
                <span className="text-image-charcoal">Trigger:</span>{' '}
                <span className="text-image-black">add tag </span>
                <code className="bg-white px-1.5 py-0.5 rounded text-image-pink">{seq.trigger_tag}</code>
                <span className="text-image-black"> in GHL</span>
              </div>
              <div>
                <span className="text-image-charcoal">Goal-tag exits:</span>{' '}
                {seq.goal_tags.map((g) => (
                  <code key={g} className="bg-white px-1.5 py-0.5 rounded text-image-charcoal mr-1">{g}</code>
                ))}
              </div>
              {binding && binding.debounce_minutes > 0 && (
                <div>
                  <span className="text-image-charcoal">Debounce:</span>{' '}
                  <span className="text-image-black">re-enrollment within {Math.floor(binding.debounce_minutes / 1440)} days is suppressed</span>
                </div>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-2">
                Sequence steps
              </div>
              <ol className="space-y-2">
                {seq.steps.map((step, i) => (
                  <li key={step.workflow_name} className="flex gap-3 items-start">
                    <span className="shrink-0 w-16 text-xs font-mono text-image-charcoal pt-0.5">
                      {'label' in step ? (step as { label?: string }).label : `Day ${step.day}`}
                    </span>
                    <span className="shrink-0 w-5 h-5 rounded-full bg-image-pink/10 text-image-pink text-[10px] font-bold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-image-black truncate">{subjectFor(step.workflow_name)}</div>
                      <code className="text-[10px] text-image-charcoal">{step.workflow_name}</code>
                    </div>
                    <Link
                      to="/admin/messages"
                      className="text-[11px] text-image-pink hover:text-image-plum font-semibold pt-0.5"
                    >
                      edit →
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-image-charcoal font-semibold mb-2">
                Currently enrolled ({active.length})
              </div>
              {active.length === 0 ? (
                <div className="text-sm text-image-charcoal italic py-3 text-center bg-image-fog/40 rounded-lg">
                  No active enrollments.
                </div>
              ) : (
                <div className="divide-y divide-image-border/60">
                  {active.map((row) => (
                    <div key={row.tenant_id} className="py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-image-black truncate">
                          {row.tenant_name ?? '(no name)'}
                          <span className="text-image-charcoal font-normal ml-2 text-xs">{row.tenant_email}</span>
                        </div>
                        <div className="text-[11px] text-image-charcoal mt-0.5">
                          Next: <strong>{row.next_step_subject}</strong>
                          {' · '}
                          {new Date(row.next_fires_at).toLocaleDateString()}
                          {' · '}
                          {row.remaining_count} of {seq.steps.length} remaining
                          {row.sent_count > 0 ? ` · ${row.sent_count} sent` : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => cancelEnrollment(row.tenant_id, seq.workflow_prefix, row.tenant_name)}
                        className="text-[11px] uppercase tracking-wider text-red-600 hover:text-red-800 font-semibold shrink-0"
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="card text-xs text-image-charcoal space-y-1">
        <div className="font-semibold text-image-black">How this connects to GHL</div>
        <div>
          When the binding is enabled and the GHL universal-tag webhook is wired, adding the
          trigger tag to a contact in GHL fires <code>ghl-event-router</code> → enrollment RPC →
          drops 6 rows into the queue. The dispatcher fires each row at its scheduled time,
          checking goal tags on the contact at fire time so leads who convert mid-sequence
          self-skip remaining sends.
        </div>
        <div>
          Manual cancellation here writes the rows to <code>cancelled</code> status. Goal-tag
          exits write to <code>skipped_by_goal</code>. Both leave an audit trail in
          <code> workflow_audit_log</code>.
        </div>
      </div>
    </div>
  );
}
